import React from 'react';
import styles from './page.module.css';

/**
 * Documentation: knowledge retrieval and answer generation — collapsible sections (grouped by h2).
 */
export function RetrievalDocumentationSection() {
  return (
    <section className={styles.section} aria-labelledby="retrieval-title">
      <h1 id="retrieval-title" className={styles.title}>
        Retrieval and answers
      </h1>
      <p className={styles.subtitle}>
        How a user question becomes traceable, audit-style answers—open a section below to read more.
      </p>

      <div className={styles.collapsibleGroup}>
        <h2 className={styles.collapsibleGroupTitle}>Overview</h2>

        <details className={styles.details}>
          <summary>In plain terms</summary>
          <p className={styles.detailsText}>
            When someone asks a question in chat, we do not simply &quot;paste top search hits&quot; into a model.
            We treat it like a senior consultant workflow: understand the question, plan what evidence to pull,
            retrieve from the right places, narrow noise with a reranker, assemble a structured evidence pack,
            draft an answer with a capable model, then optionally have a second model critique the draft.
          </p>
        </details>

        <details className={styles.details}>
          <summary>Why this matters for traceable project Q&amp;A</summary>
          <p className={styles.detailsText}>
            <strong>Traceability.</strong> Final context is grouped by document and chunk markers so answers can
            point back to URS, FS, RISK, TEST, SOP-style content and project documents—not anonymous blobs.
            This applies to any framework that values evidence-backed answers (GxP, GAMP, ISO, ASPICE, internal
            QMS, etc.) without being tied to one.
          </p>
          <p className={styles.detailsText}>
            <strong>Multi-bucket scope.</strong> The same pipeline can pull project chunks and named knowledge
            buckets (e.g. company policies, reference libraries, scope documents) in one run, matching how real
            programmes mix sources.
          </p>
          <p className={styles.detailsText}>
            <strong>Less hallucination risk.</strong> Reranking and a critic pass reduce confident wrong answers
            when retrieval is noisy—still not a substitute for human sign-off where required.
          </p>
        </details>

        <details className={styles.details}>
          <summary>Endpoints in the backend</summary>
          <p className={styles.detailsText}>
            <strong>askKnowledge</strong> — the established path: shared chunk loading and query expansion, then
            multi-query embedding similarity and a single completion with the long domain-neutral
            structured-documentation system prompt.
          </p>
          <p className={styles.detailsText}>
            <strong>retrieveKnowledge</strong> — same request shape and document scope as askKnowledge, but runs
            the full staged pipeline (analysis, plan, vector pool, TEI rerank, structured context, reasoning model,
            optional critic). The JSON response includes <code>traceability</code>, <code>sourcesPreview</code>,
            <code>stages</code> timings, and optional <code>criticNotes</code>.
          </p>
        </details>
      </div>

      <div className={styles.collapsibleGroup}>
        <h2 className={styles.collapsibleGroupTitle}>Pipeline stages</h2>

        <details className={styles.details}>
          <summary>1. Query expansion</summary>
          <p className={styles.detailsText}>
            The user question is turned into several variants (synonyms, abbreviations vs full forms, related
            concepts) so vector search recalls more of the right chunks—especially for URS/FS/RISK jargon. The
            expansion stays in the question&apos;s own language and does not assume a specific industry.
          </p>
        </details>

        <details className={styles.details}>
          <summary>2. Query understanding (fast model)</summary>
          <p className={styles.detailsText}>
            A lightweight instruct model classifies intent, surfaces entities, and flags whether compliance
            framing and explicit traceability are needed. That feeds a small <strong>retrieval plan</strong>{' '}
            (vector vs future graph, compliance emphasis, entity priorities). If the analyser is unavailable, safe
            defaults keep the pipeline running.
          </p>
        </details>

        <details className={styles.details}>
          <summary>3. Multi-source retrieval</summary>
          <p className={styles.detailsText}>
            <strong>Vector search</strong> — embedding the expanded queries, scoring all in-scope chunks with the
            best match across variants, and keeping a wider candidate pool before reranking.
          </p>
          <p className={styles.detailsText}>
            <strong>Graph / entity expansion</strong> — reserved for pulling linked URS ↔ FS ↔ RISK ↔ TEST style
            context once your entity graph is wired; today this step is a stub returning no extra rows.
          </p>
          <p className={styles.detailsText}>
            <strong>External augmentation</strong> — optional hook (e.g. web or third-party API) only when you
            deliberately enable it; not used for core regulated reasoning or sensitive project text.
          </p>
        </details>

        <details className={styles.details}>
          <summary>4. Reranking</summary>
          <p className={styles.detailsText}>
            A cross-encoder style reranker (e.g. TEI with bge-reranker-v2-m3) re-orders the candidate list so the
            reasoning model sees a tight, ordered evidence set instead of thirty semantically similar but marginal
            chunks.
          </p>
        </details>

        <details className={styles.details}>
          <summary>5. Structured context assembly</summary>
          <p className={styles.detailsText}>
            Chunks are formatted into labeled sections (per document, hints, entity references where stored) so
            the generator sees a clear workspace rather than one long concatenation.
          </p>
        </details>

        <details className={styles.details}>
          <summary>6. Reasoning and critic</summary>
          <p className={styles.detailsText}>
            A larger instruct model drafts the answer with strict grounding in the assembled context. A second,
            faster model can act as <strong>critic</strong>—checking gaps, consistency, and overall quality—and
            can refine the final wording.
          </p>
        </details>
      </div>

      <div className={styles.collapsibleGroup}>
        <h2 className={styles.collapsibleGroupTitle}>Configuration and operations</h2>

        <details className={styles.details}>
          <summary>Stack defaults</summary>
          <p className={styles.detailsText}>
            URLs and model names for Phi (analysis), TEI embed/rerank, reasoning, and critic are read from
            environment variables in the functions layer, aligned with your LLM stack docs. Override per deployment
            as needed.
          </p>
        </details>

        <details className={styles.details}>
          <summary>Tuning the pipeline</summary>
          <p className={styles.detailsText}>
            Callers of <code>retrieveKnowledge</code> can pass <code>retrievalOptions</code> to adjust pool size,
            final chunk count, context character budget, and toggles for analysis, rerank, critic, graph, or
            external augmentation without code changes.
          </p>
        </details>

        <details className={styles.details}>
          <summary>Latency and caching</summary>
          <p className={styles.detailsText}>
            Expansion and Firestore loading already run in parallel where possible; vector, rerank, and LLM calls
            dominate latency. For high-traffic prompts, consider caching embeddings for frequent queries and
            bounding <code>vectorCandidatePool</code> / <code>finalContextChunks</code>.
          </p>
        </details>
      </div>

      <div className={styles.collapsibleGroup}>
        <h2 className={styles.collapsibleGroupTitle}>Roadmap</h2>

        <details className={styles.details}>
          <summary>Stronger evidence-grade retrieval</summary>
          <p className={styles.detailsText}>
            Next increments that pay off most: real graph hops from your entity store, hybrid sparse + dense
            retrieval, uncertainty scores, automatic follow-up sub-queries, and explicit requirement-to-evidence
            matrices in context for audit-style questions.
          </p>
        </details>
      </div>
    </section>
  );
}
